import React, { useEffect, useState } from "react";
import { dateLocale, t } from "../../../i18n/locale";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { GanttData, MachineEfficiency, QualityTrend, ProcessParameter, TimelineBatch } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";
import GanttPlannerChart from "../../../components/MES/GanttPlannerChart";
import { formatClockTime } from "../../../utils/processParameterSerialize";

type TabType = "gantt" | "machines" | "quality" | "costs";

/**
 * KPI Dashboard - Gantt, Machine Efficiency, Quality Trends, Costs
 */
const KPIDashboard = () => {
  savePathToLocalStorage();

  const [activeTab, setActiveTab] = useState<TabType>("gantt");
  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate());
  const [dateFrom, setDateFrom] = useState<string>(
    new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0]
  );
  const [dateTo, setDateTo] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
  };

  // Data states
  const [ganttData, setGanttData] = useState<GanttData[]>([]);
  const [machineEfficiency, setMachineEfficiency] = useState<MachineEfficiency[]>([]);
  const [qualityTrends, setQualityTrends] = useState<QualityTrend[]>([]);
  const [costData, setCostData] = useState<any>(null);
  const [processParams, setProcessParams] = useState<ProcessParameter[]>([]);
  const [timelineBatches, setTimelineBatches] = useState<TimelineBatch[]>([]);

  useEffect(() => {
    loadData();
  }, [selectedDate, dateFrom, dateTo, activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const dateRange = { fromDate: dateFrom, toDate: dateTo };

      switch (activeTab) {
        case "gantt":
          // Load timeline for the selected date
          const timelineResponse = await mesApi.getTimelineByDate(selectedDate);
          if (timelineResponse && timelineResponse.TimelineBatch) {
            setTimelineBatches(timelineResponse.TimelineBatch);
          } else {
            setTimelineBatches([]);
          }
          break;

        case "machines":
          const machines = await mesApi.getMachineEfficiency(dateRange);
          setMachineEfficiency(machines);
          const machineParams = await mesApi.getProcessParameters({ production_date: selectedDate });
          setProcessParams(machineParams);
          break;

        case "quality":
          const quality = await mesApi.getQualityTrends(dateRange);
          setQualityTrends(quality);
          break;

        case "costs":
          const costs = await mesApi.getCostAnalysis(dateRange);
          setCostData(costs);
          break;
      }
    } catch (err: any) {
      toast.error(t("Greska pri ucitavanju: {message}", { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr?: string) => formatClockTime(dateStr);

  const formatDuration = (minutes?: number) => {
    if (!minutes) return "-";
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency < 50) return "#e74c3c";
    if (efficiency < 75) return "#f39c12";
    return "#27ae60";
  };

  const timeToMinutes = (timeStr?: string): number | null => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  };

  const minutesToTime = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  const renderGanttTab = () => {
    if (timelineBatches.length === 0) {
      return (
        <div className="empty-state-container">
          <p className="empty-state">{t("Nema potvrdjenog plana za {date}.", { date: new Date(selectedDate).toLocaleDateString(dateLocale()) })}</p>
          <p style={{ color: "#adb3bd", fontSize: "0.9rem", marginTop: "0.5rem" }}>
            {t("Kreirajte i potvrdite plan u Planiranje sekciji.")}
          </p>
        </div>
      );
    }

    return <GanttPlannerChart batches={timelineBatches} selectedDate={selectedDate} />;
  };

  const renderGanttTabOld = () => {
    if (ganttData.length === 0 && processParams.length === 0) {
      return <p className="empty-state">{t("Nema podataka za {date}.", { date: selectedDate })}</p>;
    }

    // Use process params to build timeline
    const batches = processParams.map((p) => ({
      id_zamjesa: p.MixCode,
      product_name: p.ProductName,
      mixer_name: (p as any).mixer_name || "-",
      start_mijesanje: (p as any).t_start_mijesanja,
      end_mijesanje: (p as any).t_kraj_mijesanja,
      start_pecenje: (p as any).t_start_pecenje,
      end_pecenje: (p as any).t_kraj_pecenje,
      masa_kg: p.DoughMassKg,
    }));

    // Calculate time range for Gantt chart
    let minTime = 24 * 60; // Start at end of day
    let maxTime = 0; // Start at beginning of day

    batches.forEach((b) => {
      const startMij = timeToMinutes(b.start_mijesanje);
      const endMij = timeToMinutes(b.end_mijesanje);
      const startPec = timeToMinutes(b.start_pecenje);
      const endPec = timeToMinutes(b.end_pecenje);

      [startMij, endMij, startPec, endPec].forEach((t) => {
        if (t !== null) {
          minTime = Math.min(minTime, t);
          maxTime = Math.max(maxTime, t);
        }
      });
    });

    // Default to 6:00-18:00 if no times found
    if (minTime >= maxTime) {
      minTime = 6 * 60;
      maxTime = 18 * 60;
    } else {
      // Add padding
      minTime = Math.max(0, minTime - 30);
      maxTime = Math.min(24 * 60, maxTime + 30);
    }

    const timeRange = maxTime - minTime;
    const chartWidth = 800;
    const barHeight = 24;
    const rowHeight = 36;
    const labelWidth = 120;

    // Generate hour markers
    const hourMarkers: number[] = [];
    const startHour = Math.floor(minTime / 60);
    const endHour = Math.ceil(maxTime / 60);
    for (let h = startHour; h <= endHour; h++) {
      hourMarkers.push(h * 60);
    }

    const getBarX = (minutes: number) => {
      return labelWidth + ((minutes - minTime) / timeRange) * (chartWidth - labelWidth);
    };

    const getBarWidth = (start: number, end: number) => {
      return ((end - start) / timeRange) * (chartWidth - labelWidth);
    };

    const phaseColors = {
      mijesanje: "#4b9987",
      pecenje: "#e67e22",
    };

    return (
      <>
        <p style={{ color: "#adb3bd", marginBottom: "1rem" }}>
          {t("{count} zamjesa - {date}", { count: batches.length, date: new Date(selectedDate).toLocaleDateString(dateLocale()) })}
        </p>

        {/* Visual Gantt Chart */}
        <div className="gantt-container" style={{ overflowX: "auto", marginBottom: "2rem" }}>
          <svg
            width={chartWidth}
            height={batches.length * rowHeight + 50}
            style={{ minWidth: chartWidth }}
          >
            {/* Time axis */}
            <g className="gantt-axis">
              {hourMarkers.map((h) => {
                const x = getBarX(h);
                return (
                  <g key={h}>
                    <line
                      x1={x}
                      y1={25}
                      x2={x}
                      y2={batches.length * rowHeight + 30}
                      stroke="rgba(255,255,255,0.1)"
                      strokeDasharray="2,4"
                    />
                    <text
                      x={x}
                      y={18}
                      textAnchor="middle"
                      fill="#adb3bd"
                      fontSize="11"
                    >
                      {minutesToTime(h)}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Batch rows */}
            {batches.map((batch, idx) => {
              const y = 35 + idx * rowHeight;
              const startMij = timeToMinutes(batch.start_mijesanje);
              const endMij = timeToMinutes(batch.end_mijesanje);
              const startPec = timeToMinutes(batch.start_pecenje);
              const endPec = timeToMinutes(batch.end_pecenje);

              return (
                <g key={batch.id_zamjesa} className="gantt-row">
                  {/* Row background */}
                  <rect
                    x={0}
                    y={y - 4}
                    width={chartWidth}
                    height={rowHeight}
                    fill={idx % 2 === 0 ? "rgba(0,0,0,0.1)" : "transparent"}
                  />

                  {/* Label */}
                  <text
                    x={10}
                    y={y + barHeight / 2 + 4}
                    fill="#e8e6e3"
                    fontSize="12"
                    fontWeight="500"
                  >
                    {batch.id_zamjesa.substring(0, 12)}
                  </text>

                  {/* Mixing bar */}
                  {startMij !== null && endMij !== null && (
                    <g className="gantt-bar mixing">
                      <rect
                        x={getBarX(startMij)}
                        y={y}
                        width={Math.max(getBarWidth(startMij, endMij), 4)}
                        height={barHeight}
                        fill={phaseColors.mijesanje}
                        rx={4}
                        opacity={0.9}
                      />
                      <title>{t("Mijesanje: {start} - {end}", { start: batch.start_mijesanje, end: batch.end_mijesanje })}</title>
                    </g>
                  )}

                  {/* Baking bar */}
                  {startPec !== null && endPec !== null && (
                    <g className="gantt-bar baking">
                      <rect
                        x={getBarX(startPec)}
                        y={y}
                        width={Math.max(getBarWidth(startPec, endPec), 4)}
                        height={barHeight}
                        fill={phaseColors.pecenje}
                        rx={4}
                        opacity={0.9}
                      />
                      <title>{t("Pecenje: {start} - {end}", { start: batch.start_pecenje, end: batch.end_pecenje })}</title>
                    </g>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: "2rem", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{ width: 16, height: 16, background: phaseColors.mijesanje, borderRadius: 3 }}></div>
            <span style={{ color: "#adb3bd", fontSize: "0.85rem" }}>{t("Mijesanje")}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{ width: 16, height: 16, background: phaseColors.pecenje, borderRadius: 3 }}></div>
            <span style={{ color: "#adb3bd", fontSize: "0.85rem" }}>{t("Pecenje")}</span>
          </div>
        </div>

        {/* Data Table */}
        <h4>{t("Detalji")}</h4>
        <div style={{ overflowX: "auto" }}>
          <table className="mes-table">
            <thead>
              <tr>
                <th>{t("Zamjes")}</th>
                <th>{t("Proizvod")}</th>
                <th>{t("Mikser")}</th>
                <th>{t("Start mij.")}</th>
                <th>{t("Kraj mij.")}</th>
                <th>{t("Start pec.")}</th>
                <th>{t("Kraj pec.")}</th>
                <th>{t("Masa (kg)")}</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b, idx) => (
                <tr key={idx}>
                  <td><code className="batch-id-code">{b.id_zamjesa}</code></td>
                  <td>{b.product_name || "-"}</td>
                  <td><span className="mixer-badge">{b.mixer_name}</span></td>
                  <td>{b.start_mijesanje || "-"}</td>
                  <td>{b.end_mijesanje || "-"}</td>
                  <td>{b.start_pecenje || "-"}</td>
                  <td>{b.end_pecenje || "-"}</td>
                  <td>{b.masa_kg || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderMachinesTab = () => {
    // Calculate efficiency from process params
    const machineStats: Record<string, {
      name: string;
      totalBatches: number;
      workingMinutes: number;
      idleMinutes: number;
      totalMassKg: number;
    }> = {};

    processParams.forEach((p) => {
      const mixerName = (p as any).mixer_name || "Mikser";
      if (!machineStats[mixerName]) {
        machineStats[mixerName] = {
          name: mixerName,
          totalBatches: 0,
          workingMinutes: 0,
          idleMinutes: 0,
          totalMassKg: 0,
        };
      }

      machineStats[mixerName].totalBatches++;
      machineStats[mixerName].totalMassKg += p.DoughMassKg || 0;

      // Calculate working time from start/end times if available
      const startMij = (p as any).t_start_mijesanja;
      const endMij = (p as any).t_kraj_mijesanja;
      if (startMij && endMij) {
        const [sh, sm] = startMij.split(":").map(Number);
        const [eh, em] = endMij.split(":").map(Number);
        const duration = (eh * 60 + em) - (sh * 60 + sm);
        if (duration > 0) {
          machineStats[mixerName].workingMinutes += duration;
        }
      }
    });

    const stats = Object.values(machineStats);

    if (stats.length === 0) {
      return <p className="empty-state">{t("Nema podataka o strojevima za odabrani period.")}</p>;
    }

    // Production shift: 04:00-22:00 (18 hours)
    const SHIFT_START_H = 4;
    const SHIFT_END_H = 22;
    const shiftMinutes = (SHIFT_END_H - SHIFT_START_H) * 60; // 1080 min

    return (
      <>
        <h4>{t("Efikasnost strojeva")}</h4>
        <div className="mes-table-wrap">
          <table className="mes-table">
          <thead>
            <tr>
              <th>{t("Stroj")}</th>
              <th>{t("Zamjesa")}</th>
              <th>{t("Ukupno radi (min)")}</th>
              <th>{t("Ukupno stoji (min)")}</th>
              <th>{t("Efikasnost (%)")}</th>
              <th>{t("Ukupna masa (kg)")}</th>
            </tr>
          </thead>
          <tbody>
            {stats.map((s, idx) => {
              const efficiency = shiftMinutes > 0 ? (s.workingMinutes / shiftMinutes) * 100 : 0;
              return (
                <tr key={idx}>
                  <td><span className="mixer-badge">{s.name}</span></td>
                  <td>{s.totalBatches}</td>
                  <td>{s.workingMinutes}</td>
                  <td>{shiftMinutes - s.workingMinutes}</td>
                  <td style={{
                    background: efficiency < 50 ? "#fadbd8" : efficiency < 75 ? "#fef9e7" : "#d5f5e3",
                    color: "#000",
                    fontWeight: 500,
                  }}>
                    {efficiency.toFixed(1)}%
                  </td>
                  <td>{s.totalMassKg.toFixed(1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* Throughput */}
        <div style={{ marginTop: "2rem" }}>
          <h4>{t("Propusnost (komadi/h)")}</h4>
          <p style={{ color: "#adb3bd" }}>
            {t("Potrebni podaci o broju komada po zamjesu za izracun propusnosti.")}
          </p>
        </div>
      </>
    );
  };

  const renderQualityTab = () => {
    if (qualityTrends.length === 0) {
      return <p className="empty-state">{t("Nema podataka o kvaliteti za odabrani period.")}</p>;
    }

    // Group by product for analysis
    const byProduct: Record<string, QualityTrend[]> = {};
    qualityTrends.forEach((q) => {
      const name = q.ProductName || "Ostalo";
      if (!byProduct[name]) byProduct[name] = [];
      byProduct[name].push(q);
    });

    const products = Object.keys(byProduct).sort();

    return (
      <>
        <div style={{ marginBottom: "1rem" }}>
          <p style={{ color: "#adb3bd" }}>
            {t("{count} zapisa u periodu {from} - {to}", { count: qualityTrends.length, from: new Date(dateFrom).toLocaleDateString(dateLocale()), to: new Date(dateTo).toLocaleDateString(dateLocale()) })}
          </p>
        </div>

        {/* Quality Metrics Table */}
        <div className="mes-table-wrap">
          <table className="mes-table">
          <thead>
            <tr>
              <th>{t("Datum")}</th>
              <th>{t("Zamjes")}</th>
              <th>{t("Proizvod")}</th>
              <th>{t("Temp (°C)")}</th>
              <th>{t("pH starter")}</th>
              <th>{t("Masa (kg)")}</th>
              <th>{t("Skart %")}</th>
            </tr>
          </thead>
          <tbody>
            {qualityTrends.slice(0, 50).map((q, idx) => (
              <tr key={idx}>
                <td>{new Date(q.ProductionDate).toLocaleDateString(dateLocale())}</td>
                <td><code className="batch-id-code">{q.MixCode}</code></td>
                <td>{q.ProductName || "-"}</td>
                <td>{q.DoughTempC || "-"}</td>
                <td>{q.StarterPh || "-"}</td>
                <td>{q.DoughMassKg || "-"}</td>
                <td style={{
                  color: (q.ScrapPct || 0) > 5 ? "#e74c3c" : (q.ScrapPct || 0) > 2 ? "#f39c12" : "#27ae60",
                }}>
                  {q.ScrapPct ? `${q.ScrapPct.toFixed(2)}%` : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Trend Chart Placeholder */}
        <div style={{ marginTop: "2rem" }}>
          <h4>{t("Trend analiza")}</h4>
          <div style={{
            background: "rgba(0,0,0,0.2)",
            padding: "2rem",
            borderRadius: "8px",
            textAlign: "center",
            minHeight: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <div>
              <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>📈</div>
              <p>Trend chart placeholder</p>
              <p style={{ fontSize: "0.85rem", color: "#adb3bd" }}>
                {t("Box plot / line chart za praćenje meta parametara kroz vrijeme")}
              </p>
            </div>
          </div>
        </div>

        {/* Correlation Analysis */}
        <div style={{ marginTop: "2rem" }}>
          <h4>{t("Korelacije meta parametara")}</h4>
          <p style={{ color: "#adb3bd" }}>
            {t("Pearsonova korelacija - crveno = negativna, plavo = pozitivna")}
          </p>
          <div style={{
            background: "rgba(0,0,0,0.2)",
            padding: "2rem",
            borderRadius: "8px",
            textAlign: "center",
          }}>
            <p>{t("Heatmap korelacija - placeholder")}</p>
          </div>
        </div>
      </>
    );
  };

  const renderCostsTab = () => {
    return (
      <>
        <p style={{ color: "#adb3bd", marginBottom: "1rem" }}>
          {t("Troskovi sirovina, pakiranja i VPC po zamjesu. Cijene se uzimaju prema datumu proizvodnje.")}
        </p>

        {/* Summary Metrics */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
          <div className="mes-metric-card">
            <div className="metric-label">{t("Ukupni trosak sirovina")}</div>
            <div className="metric-value">{costData?.totalIngredientCost?.toFixed(2) || "-"} EUR</div>
          </div>
          <div className="mes-metric-card">
            <div className="metric-label">{t("Ukupni trosak pakiranja")}</div>
            <div className="metric-value">{costData?.totalPackagingCost?.toFixed(2) || "-"} EUR</div>
          </div>
          <div className="mes-metric-card">
            <div className="metric-label">{t("Ukupna VPC")}</div>
            <div className="metric-value">{costData?.totalVPC?.toFixed(2) || "-"} EUR</div>
          </div>
          <div className="mes-metric-card">
            <div className="metric-label">{t("Ukupna marza")}</div>
            <div className="metric-value" style={{
              color: (costData?.totalMargin || 0) >= 0 ? "#27ae60" : "#e74c3c"
            }}>
              {costData?.totalMargin?.toFixed(2) || "-"} EUR
            </div>
          </div>
        </div>

        {/* Cost Details Table */}
        {costData?.details ? (
          <div className="mes-table-wrap">
            <table className="mes-table">
            <thead>
              <tr>
                <th>{t("Datum")}</th>
                <th>{t("Proizvod")}</th>
                <th>{t("Zamjesa")}</th>
                <th>{t("Sirovine (EUR)")}</th>
                <th>{t("Pakiranje (EUR)")}</th>
                <th>{t("VPC (EUR)")}</th>
                <th>{t("Ukupni trosak")}</th>
                <th>{t("Marza (EUR)")}</th>
                <th>{t("Marza (%)")}</th>
              </tr>
            </thead>
            <tbody>
              {costData.details.map((d: any, idx: number) => (
                <tr key={idx}>
                  <td>{new Date(d.production_date).toLocaleDateString(dateLocale())}</td>
                  <td>{d.product_name || "-"}</td>
                  <td>{d.batch_count || "-"}</td>
                  <td>{d.ingredient_cost?.toFixed(2) || "-"}</td>
                  <td>{d.packaging_cost?.toFixed(2) || "-"}</td>
                  <td>{d.vpc?.toFixed(2) || "-"}</td>
                  <td>{d.total_cost?.toFixed(2) || "-"}</td>
                  <td style={{ color: (d.margin || 0) >= 0 ? "#27ae60" : "#e74c3c" }}>
                    {d.margin?.toFixed(2) || "-"}
                  </td>
                  <td style={{
                    background: (d.margin_pct || 0) < 0 ? "#fadbd8" :
                                (d.margin_pct || 0) < 15 ? "#fef9e7" : "#d5f5e3",
                    color: "#000",
                  }}>
                    {d.margin_pct?.toFixed(1) || "-"}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <p className="empty-state">{t("Nema detaljnih podataka o troskovima za odabrani period.")}</p>
        )}
      </>
    );
  };

  return (
    <div className="mes-page">
      <Header title={t("MES - KPI Dashboard")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Date Selector */}
          <MesDateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            showTimelineIndicators={true}
          />

          {/* Date Range Filters for Trend Analysis */}
          <div className="mes-section">
            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
              <div className="form-group">
                <label>{t("Od datuma (trend)")}</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input-classic date-input"
                />
              </div>
              <div className="form-group">
                <label>{t("Do datuma (trend)")}</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input-classic date-input"
                />
              </div>
              <button
                className="btn-primary"
                onClick={loadData}
                style={{ marginBottom: "0.5rem" }}
              >
                {t("Osvjezi")}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mes-section">
            <div className="kpi-tabs" style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
              <button
                className={`kpi-tab ${activeTab === "gantt" ? "active" : ""}`}
                onClick={() => setActiveTab("gantt")}
              >
                📊 Gantt
              </button>
              <button
                className={`kpi-tab ${activeTab === "machines" ? "active" : ""}`}
                onClick={() => setActiveTab("machines")}
              >
                ⚙️ {t("Efikasnost strojeva")}
              </button>
              <button
                className={`kpi-tab ${activeTab === "quality" ? "active" : ""}`}
                onClick={() => setActiveTab("quality")}
              >
                🔬 {t("Kvaliteta")}
              </button>
              <button
                className={`kpi-tab ${activeTab === "costs" ? "active" : ""}`}
                onClick={() => setActiveTab("costs")}
              >
                💰 {t("Cijene & VPC")}
              </button>
            </div>

            {loading ? (
              <p>{t("Ucitavanje...")}</p>
            ) : (
              <>
                {activeTab === "gantt" && renderGanttTab()}
                {activeTab === "machines" && renderMachinesTab()}
                {activeTab === "quality" && renderQualityTab()}
                {activeTab === "costs" && renderCostsTab()}
              </>
            )}
          </div>
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default KPIDashboard;
